/* Avenora wishlist — Gymshark-style saved items without an app.
   Handles live in localStorage; the header heart shows a count; the
   /pages/wishlist page renders saved products from /products/{handle}.js.
   Product cards get a heart button: guests see a save-to-wishlist popup
   (create account / log in), logged-in customers get an instant save with
   a heart-burst animation and one heart flying to the header icon. */
(() => {
  const KEY = 'avenora:wishlist';
  const EVENT = 'avenora:wishlist:change';
  const loggedIn = window.avenoraCustomerLoggedIn === true;

  const read = () => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  };
  const write = (handles) => {
    localStorage.setItem(KEY, JSON.stringify(handles));
    document.dispatchEvent(new CustomEvent(EVENT));
  };
  const has = (handle) => read().includes(handle);
  const toggle = (handle) => {
    const list = read();
    const next = list.includes(handle) ? list.filter((h) => h !== handle) : [...list, handle];
    write(next);
    return next.includes(handle);
  };

  const HEART_PATH = 'M12 20.5C6.5 16 2 12.4 2 8.2 2 5.3 4.3 3 7.2 3c1.8 0 3.5 1 4.3 2.6l.5.9.5-.9A5.2 5.2 0 0 1 16.8 3 4.5 4.5 0 0 1 21.3 8.2c0 4.2-4.4 7.8-9.3 12.3Z';
  const HEART = (filled) => `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 22" fill="${filled ? 'currentColor' : 'none'}" aria-hidden="true">
      <path stroke="currentColor" stroke-linejoin="round" d="${HEART_PATH}"/>
    </svg>`;

  const style = document.createElement('style');
  style.textContent = `
    .avenora-wishlist-link { position: relative; display: inline-flex; align-items: center; }
    /* Header heart: one pixel thicker than the theme's icon stroke */
    .avenora-wishlist-link svg path { stroke-width: calc(var(--icon-stroke-width, 1.5px) + 1px); }
    .avenora-wishlist-count { position: absolute; top: -6px; right: -8px; min-width: 15px; height: 15px;
      border-radius: 999px; background: currentColor; font-size: 10px; line-height: 15px; text-align: center;
      padding: 0 3px; }
    .avenora-wishlist-count > span { color: var(--color-background, #fff); display: block; }
    .avenora-wishlist-toggle { display: inline-flex; align-items: center; gap: 8px; background: none;
      border: 1px solid currentColor; border-radius: 999px; padding: 8px 18px; margin-block: 12px;
      cursor: pointer; font: inherit; color: inherit; }
    .avenora-wishlist-toggle:hover { opacity: .7; }
    .avenora-wishlist-toggle svg { width: 17px; height: 16px; color: #7C3AED; }
    .avenora-wishlist-toggle svg path { stroke-width: calc(var(--icon-stroke-width, 1.5px) + 1px); }

    /* Card hearts: the thicker heart at 75% of the header size, top-right of
       every product card (Gymshark placement) */
    .product-card { position: relative; }
    .avenora-card-heart { position: absolute; top: 10px; right: 10px; z-index: 4; width: 32px; height: 32px;
      display: grid; place-items: center; background: none; border: none; padding: 0; cursor: pointer;
      color: #7C3AED; }
    .avenora-card-heart svg { width: 15px; height: 14px; pointer-events: none;
      filter: drop-shadow(0 0 2px rgba(255,255,255,0.8)); }
    .avenora-card-heart svg path { stroke-width: calc(var(--icon-stroke-width, 1.5px) + 1px); }
    .avenora-card-heart:hover svg { transform: scale(1.15); }

    /* Guest popup */
    .avenora-wl-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.55); z-index: 200;
      display: grid; place-items: center; }
    .avenora-wl-modal { background: #fff; color: #1c1b19; max-width: 480px; width: calc(100% - 40px);
      padding: 0 0 28px; text-align: center; position: relative; }
    .avenora-wl-modal-head { display: flex; align-items: center; justify-content: center; gap: 10px;
      padding: 22px 52px; border-bottom: 1px solid #eee; font-weight: 700; letter-spacing: 0.06em;
      font-size: 1.05rem; text-transform: uppercase; }
    .avenora-wl-modal-head svg { width: 20px; height: 18px; }
    .avenora-wl-close { position: absolute; top: 16px; right: 16px; background: none; border: none;
      font-size: 22px; line-height: 1; cursor: pointer; color: #1c1b19; }
    .avenora-wl-modal p { margin: 24px 28px 22px; font-size: 0.95rem; line-height: 1.5; color: #444; text-align: center; }
    .avenora-wl-actions { display: flex; gap: 12px; justify-content: center; padding: 0 28px; }
    .avenora-wl-actions a { flex: 1; padding: 14px 0; font-weight: 600; font-size: 0.95rem;
      text-decoration: none; border: 1px solid #1c1b19; }
    .avenora-wl-actions a.primary { background: #1c1b19; color: #fff; }
    .avenora-wl-actions a.secondary { background: #fff; color: #1c1b19; }

    /* Heart animations */
    @keyframes avenora-heart-burst {
      0% { transform: translate(0, 0) scale(0.6); opacity: 1; }
      100% { transform: translate(var(--dx), var(--dy)) scale(1.15); opacity: 0; }
    }
    .avenora-heart-particle { position: fixed; z-index: 210; width: 14px; height: 13px; color: #e0526e;
      pointer-events: none; animation: avenora-heart-burst 700ms ease-out forwards; }
    .avenora-heart-flyer { position: fixed; z-index: 210; width: 16px; height: 15px; color: #e0526e;
      pointer-events: none; transition: transform 800ms cubic-bezier(0.3, 0, 0.4, 1), opacity 800ms ease; }
    @media (prefers-reduced-motion: reduce) {
      .avenora-heart-particle, .avenora-heart-flyer { display: none; }
    }

    /* Wishlist page grid */
    .avenora-wishlist-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 24px 16px; padding-block: 8px 40px; }
    .avenora-wishlist-grid .avenora-wishlist-card { position: relative; }
    .avenora-wishlist-card img { width: 100%; aspect-ratio: 3 / 4; object-fit: cover; display: block; }
    .avenora-wishlist-card a { text-decoration: none; color: inherit; }
    .avenora-wishlist-card h3 { font-size: 1rem; font-weight: 400; margin: 10px 0 2px; }
    .avenora-wishlist-card .price { font-weight: 600; }
    .avenora-wishlist-card .remove { position: absolute; top: 10px; right: 10px; background: #fff;
      border: none; border-radius: 999px; width: 34px; height: 34px; display: grid; place-items: center;
      cursor: pointer; color: #1a1a1a; box-shadow: 0 1px 4px rgba(0,0,0,.15); }
    .avenora-wishlist-card .remove svg { width: 16px; height: 15px; }
    .avenora-wishlist-empty { padding: 48px 0 64px; }
    .avenora-wishlist-empty a { text-decoration: underline; }
  `;
  document.head.appendChild(style);

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.render();
      document.addEventListener(EVENT, () => this.render());
      window.addEventListener('storage', (e) => { if (e.key === KEY) this.render(); });
    }
    render() {
      const n = read().length;
      this.hidden = n === 0;
      this.innerHTML = `<span>${n}</span>`;
    }
  }
  customElements.define('avenora-wishlist-count', WishlistCount);

  // --- Guest popup -----------------------------------------------------------
  function openWishlistModal() {
    if (document.querySelector('.avenora-wl-overlay')) return;
    const overlay = document.createElement('div');
    overlay.className = 'avenora-wl-overlay';
    overlay.innerHTML = `
      <div class="avenora-wl-modal" role="dialog" aria-modal="true" aria-label="Save to wishlist">
        <div class="avenora-wl-modal-head">${HEART(true)} Save to Wishlist</div>
        <button class="avenora-wl-close" type="button" aria-label="Close">&times;</button>
        <p>Keep all your favorite pieces in one place and come back to them any time — that's what the wishlist is for!<br>Log in or create a free account to start saving.</p>
        <div class="avenora-wl-actions">
          <a class="primary" href="/account/login">Sign in or create account</a>
        </div>
      </div>`;
    const close = () => overlay.remove();
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    overlay.querySelector('.avenora-wl-close').addEventListener('click', close);
    document.addEventListener('keydown', function esc(e) {
      if (e.key === 'Escape') { close(); document.removeEventListener('keydown', esc); }
    });
    document.body.appendChild(overlay);
  }

  // --- Animations ------------------------------------------------------------
  function heartAt(rect, cls) {
    const el = document.createElement('span');
    el.className = cls;
    el.innerHTML = HEART(true);
    el.style.left = `${rect.left + rect.width / 2 - 8}px`;
    el.style.top = `${rect.top + rect.height / 2 - 8}px`;
    document.body.appendChild(el);
    return el;
  }

  function celebrate(button) {
    const rect = button.getBoundingClientRect();
    // Little hearts flying all around the item
    for (let i = 0; i < 7; i += 1) {
      const angle = (Math.PI * 2 * i) / 7 + Math.random() * 0.6;
      const dist = 42 + Math.random() * 46;
      const p = heartAt(rect, 'avenora-heart-particle');
      p.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
      p.style.setProperty('--dy', `${Math.sin(angle) * dist - 18}px`);
      setTimeout(() => p.remove(), 750);
    }
    // One heart flies to the header wishlist icon
    const target = document.querySelector('.avenora-wishlist-link');
    if (target) {
      const to = target.getBoundingClientRect();
      const flyer = heartAt(rect, 'avenora-heart-flyer');
      requestAnimationFrame(() => {
        const dx = to.left + to.width / 2 - (rect.left + rect.width / 2);
        const dy = to.top + to.height / 2 - (rect.top + rect.height / 2);
        flyer.style.transform = `translate(${dx}px, ${dy}px) scale(0.55)`;
        flyer.style.opacity = '0.25';
      });
      setTimeout(() => flyer.remove(), 850);
    }
  }

  // --- Product-card hearts ---------------------------------------------------
  function handleFromCard(card) {
    const link = card.querySelector('a[href*="/products/"]');
    const match = link?.getAttribute('href')?.match(/\/products\/([a-z0-9-]+)/);
    return match ? match[1] : null;
  }

  function paintCardHeart(btn, saved) {
    btn.innerHTML = HEART(saved);
    btn.setAttribute('aria-pressed', String(saved));
    btn.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Save to wishlist');
  }

  function initCardHearts(root = document) {
    root.querySelectorAll('product-card, .product-card').forEach((card) => {
      if (card.querySelector('.avenora-card-heart')) return;
      const handle = handleFromCard(card);
      if (!handle) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'avenora-card-heart';
      paintCardHeart(btn, has(handle));
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!loggedIn) { openWishlistModal(); return; }
        const saved = toggle(handle);
        paintCardHeart(btn, saved);
        if (saved) celebrate(btn);
      });
      card.appendChild(btn);
    });
  }

  // Keep saved-state in sync across cards, tabs, and the header count.
  document.addEventListener(EVENT, () => {
    document.querySelectorAll('.avenora-card-heart').forEach((btn) => {
      const handle = handleFromCard(btn.closest('product-card, .product-card'));
      if (handle) paintCardHeart(btn, has(handle));
    });
  });

  // Product page: heart toggle next to the product title (same login gate).
  const initProductToggle = () => {
    const match = window.location.pathname.match(/\/products\/([a-z0-9-]+)/);
    if (!match) return;
    const handle = match[1];
    const title = document.querySelector('product-information h1, main h1');
    if (!title || document.querySelector('.avenora-wishlist-toggle')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'avenora-wishlist-toggle';
    const paint = (saved) => {
      btn.innerHTML = `${HEART(saved)}<span>${saved ? 'Saved to wishlist' : 'Add to wishlist'}</span>`;
      btn.setAttribute('aria-pressed', String(saved));
    };
    paint(has(handle));
    btn.addEventListener('click', () => {
      if (!loggedIn) { openWishlistModal(); return; }
      const saved = toggle(handle);
      paint(saved);
      if (saved) celebrate(btn);
    });
    title.insertAdjacentElement('afterend', btn);
  };

  // Wishlist page: render saved products.
  class WishlistGrid extends HTMLElement {
    async connectedCallback() {
      const handles = read();
      if (!handles.length) {
        this.innerHTML = `<div class="avenora-wishlist-empty"><p>Your wishlist is empty.</p>
          <p><a href="/collections/women">Shop Women</a> · <a href="/collections/men">Shop Men</a></p></div>`;
        return;
      }
      const products = (await Promise.all(handles.map(async (h) => {
        try { return await (await fetch(`/products/${h}.js`)).json(); } catch { return null; }
      }))).filter(Boolean);
      this.innerHTML = `<div class="avenora-wishlist-grid">${products.map((p) => `
        <div class="avenora-wishlist-card" data-handle="${p.handle}">
          <a href="${p.url}">
            <img src="${(p.featured_image || '').replace(/^\/\//, 'https://')}" alt="" loading="lazy">
            <h3>${p.title}</h3>
            <div class="price">$${p.price % 100 === 0 ? p.price / 100 : (p.price / 100).toFixed(2)}</div>
          </a>
          <button class="remove" type="button" aria-label="Remove ${p.title} from wishlist">${HEART(true)}</button>
        </div>`).join('')}</div>`;
      this.querySelectorAll('.remove').forEach((el) => el.addEventListener('click', () => {
        toggle(el.closest('.avenora-wishlist-card').dataset.handle);
        this.connectedCallback();
      }));
    }
  }
  customElements.define('avenora-wishlist-grid', WishlistGrid);

  const boot = () => {
    initProductToggle();
    initCardHearts();
    // Filters/pagination re-render grids without a navigation — re-scan.
    const observer = new MutationObserver(() => initCardHearts());
    observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
